import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      rootMargin: `-${Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--skull-promo-top-height')) || 0}px 0px 0px 0px`,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = entry.intersectionRatio >= 1 ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handlePromoLayout = () => {
    const sticky = this.getAttribute('sticky');
    if (!sticky) return;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(sticky === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.addEventListener('skull:promo-layout', this.#handlePromoLayout);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('skull:promo-layout', this.#handlePromoLayout);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      // A resize callback may include only the new promo strip. Recount every
      // child so changing that strip does not replace the full group height.
      const headerGroupHeight = Array.from(headerGroup.children).reduce((total, element) => {
        if (!(element instanceof HTMLElement)) return total;
        return total + element.offsetHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});




 (() => {
    if (window.SBShippingInsuranceReady) return;
    window.SBShippingInsuranceReady = true;

    const SB_INSURANCE_VARIANT_ID = '68074549084388';
    const SB_INSURANCE_DECLINED_KEY = 'sb_shipping_insurance_declined';

    let isUpdatingInsurance = false;
    let isAutoAddingInsurance = false;
    let originalFetch = window.fetch;

    function getRoutesRoot() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }

      if (window.routes && window.routes.root_url) {
        return window.routes.root_url;
      }

      return '/';
    }

    function getCartUrl() {
      if (window.routes && window.routes.cart_url) return window.routes.cart_url;
      return '/cart';
    }

    function normalizeUrl(input) {
      try {
        if (typeof input === 'string') return input;
        if (input && input.url) return input.url;
      } catch (error) {}

      return '';
    }

    function isCartAddUrl(url) {
      return /\/cart\/add(\.js)?(\?|$)/.test(url);
    }

    function isCartChangeUrl(url) {
      return /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url);
    }

    function isInsuranceAddRequest(input, init) {
      try {
        const body = init && init.body ? init.body : input && input.body;
        if (!body) return false;

        const raw = typeof body === 'string' ? body : '';
        return raw.includes(SB_INSURANCE_VARIANT_ID);
      } catch (error) {
        return false;
      }
    }

    async function getCart() {
      const response = await originalFetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('No se pudo leer el carrito');
      }

      return response.json();
    }

    function findInsuranceItem(cart) {
      if (!cart || !Array.isArray(cart.items)) return null;

      return cart.items.find((item) => {
        return String(item.variant_id) === String(SB_INSURANCE_VARIANT_ID);
      }) || null;
    }

    function countNonInsuranceItems(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      return cart.items.reduce((total, item) => {
        if (String(item.variant_id) === String(SB_INSURANCE_VARIANT_ID)) return total;
        return total + item.quantity;
      }, 0);
    }

    function updateInsuranceCopy(isActive) {
      document.querySelectorAll('[data-sb-shipping-insurance]').forEach((root) => {
        const status = root.querySelector('[data-sb-insurance-status]');
        const title = root.querySelector('[data-sb-insurance-title]');
        const price = root.querySelector('[data-sb-insurance-price]');
        const desc = root.querySelector('[data-sb-insurance-desc]');

        if (!status || !title || !price || !desc) return;

        if (isActive) {
          status.textContent = 'Seguro de envio | Robo · Perdida · Daño';
          title.textContent = 'Envio protegido Activado';
          price.textContent = 'Recomendado $99 MXN';
          desc.textContent = '';
        } else {
          status.textContent = 'Envio no protegido';
          title.textContent = 'Activa proteccion de envio';
          price.textContent = '$99 MXN | Robo · Perdida · Daño';
          desc.textContent = '';
        }
      });
    }

    async function addInsurance() {
      const response = await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(SB_INSURANCE_VARIANT_ID),
              quantity: 1,
              properties: {
                '_tipo': 'Seguro de envío',
                '_cobertura': 'Pérdida, robo o daño'
              }
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('No se pudo agregar el seguro');
      }

      return response.json();
    }

    async function changeInsuranceQuantity(itemKey, quantity) {
      const response = await originalFetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('No se pudo actualizar el seguro');
      }

      return response.json();
    }

    function setInsuranceVisualState(isActive) {
      document.querySelectorAll('[data-sb-insurance-checkbox]').forEach((checkbox) => {
        checkbox.checked = isActive;
        if (isActive) {
          checkbox.setAttribute('checked', 'checked');
        } else {
          checkbox.removeAttribute('checked');
        }
      });

      updateInsuranceCopy(isActive);
    }

    function syncAllInsuranceSwitches(cart) {
      const hasInsurance = Boolean(findInsuranceItem(cart));
      setInsuranceVisualState(hasInsurance);
    }

    function dispatchCartEvents(cart) {
      const events = [
        'cart:update',
        'cart:updated',
        'cart:refresh',
        'theme:cart:update',
        'theme:cart:refresh'
      ];

      events.forEach((eventName) => {
        document.dispatchEvent(new CustomEvent(eventName, {
          bubbles: true,
          detail: { cart }
        }));

        window.dispatchEvent(new CustomEvent(eventName, {
          detail: { cart }
        }));

        document.documentElement.dispatchEvent(new CustomEvent(eventName, {
          bubbles: true,
          detail: { cart }
        }));
      });
    }

    function getLikelySectionIds() {
      const ids = new Set([
        'cart-drawer',
        'cart-icon-bubble',
        'cart-live-region-text',
        'main-cart-items',
        'main-cart-footer'
      ]);

      document.querySelectorAll('[id^="shopify-section-"]').forEach((section) => {
        const id = section.id.replace('shopify-section-', '');
        if (/cart|drawer|bubble|header/i.test(id)) ids.add(id);
      });

      document.querySelectorAll('cart-drawer, cart-drawer-items, cart-component, cart-items').forEach((component) => {
        const section = component.closest('[id^="shopify-section-"]');
        if (section && section.id) ids.add(section.id.replace('shopify-section-', ''));
      });

      return [...ids].filter(Boolean);
    }

    function replaceSectionHTML(sectionId, html) {
      if (!html) return false;

      const currentSection = document.getElementById(`shopify-section-${sectionId}`);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingSection = parsed.getElementById(`shopify-section-${sectionId}`);

      if (currentSection && incomingSection) {
        currentSection.innerHTML = incomingSection.innerHTML;
        return true;
      }

      const selectorsBySection = {
        'cart-drawer': ['cart-drawer', '#CartDrawer', '.cart-drawer', 'cart-component'],
        'cart-icon-bubble': ['#cart-icon-bubble', '.cart-count-bubble']
      };

      const selectors = selectorsBySection[sectionId] || [];
      for (const selector of selectors) {
        const current = document.querySelector(selector);
        const incoming = parsed.querySelector(selector);

        if (current && incoming) {
          current.innerHTML = incoming.innerHTML;
          current.className = incoming.className;
          return true;
        }
      }

      return false;
    }

    async function fetchAndReplaceCartSections() {
      const sectionIds = getLikelySectionIds();
      const sectionParam = encodeURIComponent(sectionIds.join(','));
      const cartUrl = getCartUrl();
      const separator = cartUrl.includes('?') ? '&' : '?';
      const url = `${cartUrl}${separator}sections=${sectionParam}`;

      const response = await originalFetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) return false;

      const sections = await response.json();
      let replaced = false;

      Object.keys(sections || {}).forEach((sectionId) => {
        if (replaceSectionHTML(sectionId, sections[sectionId])) replaced = true;
      });

      return replaced;
    }

    async function forceFabricCartDrawerRefresh(cart) {
      syncAllInsuranceSwitches(cart);

      const cartDrawer = document.querySelector('cart-drawer');
      const cartComponent = document.querySelector('cart-component');
      const cartItems = document.querySelector('cart-items, cart-drawer-items');

      try {
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function' && typeof cartDrawer.renderContents === 'function') {
          const sectionsToRender = cartDrawer.getSectionsToRender().map((section) => section.section || section.id).filter(Boolean);

          if (sectionsToRender.length) {
            const response = await originalFetch(`${getCartUrl()}?sections=${sectionsToRender.join(',')}`);
            const sections = await response.json();
            cartDrawer.renderContents({ ...cart, sections });
            syncAllInsuranceSwitches(cart);
            return;
          }
        }
      } catch (error) {
        console.warn('[Seguro de envío] renderContents de cart-drawer falló, usando sections:', error);
      }

      try {
        await fetchAndReplaceCartSections();
      } catch (error) {
        console.warn('[Seguro de envío] No se pudieron reemplazar sections:', error);
      }

      [cartDrawer, cartComponent, cartItems].forEach((component) => {
        if (!component) return;

        try {
          if (typeof component.renderContents === 'function') component.renderContents(cart);
          if (typeof component.refresh === 'function') component.refresh();
          if (typeof component.onCartUpdate === 'function') component.onCartUpdate(cart);
        } catch (error) {
          console.warn('[Seguro de envío] No se pudo refrescar componente:', error);
        }
      });

      dispatchCartEvents(cart);

      requestAnimationFrame(() => {
        syncAllInsuranceSwitches(cart);
      });
    }

    async function refreshCartUI(cart) {
      await forceFabricCartDrawerRefresh(cart);

      setTimeout(async () => {
        try {
          const freshCart = await getCart();
          syncAllInsuranceSwitches(freshCart);
          await forceFabricCartDrawerRefresh(freshCart);
        } catch (error) {
          console.error('[Seguro de envío] Error resincronizando switch:', error);
        }
      }, 220);
    }

    async function autoAddInsuranceAfterRealItem() {
      if (isUpdatingInsurance || isAutoAddingInsurance) return;
      if (localStorage.getItem(SB_INSURANCE_DECLINED_KEY) === '1') return;

      isAutoAddingInsurance = true;
      isUpdatingInsurance = true;

      try {
        await new Promise((resolve) => setTimeout(resolve, 80));

        const cartBefore = await getCart();
        const insuranceItem = findInsuranceItem(cartBefore);
        const nonInsuranceItems = countNonInsuranceItems(cartBefore);

        if (nonInsuranceItems === 0) return;

        if (!insuranceItem) {
          await addInsurance();
          setInsuranceVisualState(true);
        } else if (insuranceItem.quantity > 1) {
          await changeInsuranceQuantity(insuranceItem.key, 1);
          setInsuranceVisualState(true);
        } else {
          setInsuranceVisualState(true);
        }

        const cartAfter = await getCart();
        setInsuranceVisualState(true);
        await refreshCartUI(cartAfter);
      } catch (error) {
        console.error('[Seguro de envío] Error autoactivando:', error);
      } finally {
        isUpdatingInsurance = false;
        isAutoAddingInsurance = false;
      }
    }

    async function handleInsuranceToggle(checkbox) {
      if (isUpdatingInsurance) return;

      const root = checkbox.closest('[data-sb-shipping-insurance]');
      const row = root ? root.querySelector('.sb-insurance-row') : null;

      isUpdatingInsurance = true;

      if (row) {
        row.classList.add('sb-insurance-loading');
      }

      try {
        const cartBefore = await getCart();
        const existingInsurance = findInsuranceItem(cartBefore);
        const nonInsuranceItems = countNonInsuranceItems(cartBefore);
        const shouldEnable = checkbox.checked;

        if (shouldEnable) {
          localStorage.removeItem(SB_INSURANCE_DECLINED_KEY);
        } else {
          localStorage.setItem(SB_INSURANCE_DECLINED_KEY, '1');
        }

        if (shouldEnable && nonInsuranceItems === 0) {
          checkbox.checked = false;
          updateInsuranceCopy(false);
          return;
        }

        if (shouldEnable) {
          checkbox.checked = true;
          updateInsuranceCopy(true);

          if (!existingInsurance) {
            await addInsurance();
          } else if (existingInsurance.quantity > 1) {
            await changeInsuranceQuantity(existingInsurance.key, 1);
          }

          setInsuranceVisualState(true);
        } else {
          checkbox.checked = false;
          updateInsuranceCopy(false);

          if (existingInsurance) {
            await changeInsuranceQuantity(existingInsurance.key, 0);
          }
        }

        const cartAfter = await getCart();
        syncAllInsuranceSwitches(cartAfter);
        await refreshCartUI(cartAfter);
      } catch (error) {
        console.error('[Seguro de envío]', error);

        try {
          const cart = await getCart();
          syncAllInsuranceSwitches(cart);
        } catch (syncError) {
          console.error('[Seguro de envío] Error revirtiendo estado:', syncError);
          checkbox.checked = !checkbox.checked;
          updateInsuranceCopy(checkbox.checked);
        }
      } finally {
        isUpdatingInsurance = false;

        if (row) {
          row.classList.remove('sb-insurance-loading');
        }
      }
    }

    async function syncFromCart() {
      if (isUpdatingInsurance) return;

      try {
        const cart = await getCart();
        const insuranceItem = findInsuranceItem(cart);
        const nonInsuranceItems = countNonInsuranceItems(cart);

        if (insuranceItem && nonInsuranceItems === 0) {
          const cleanedCart = await changeInsuranceQuantity(insuranceItem.key, 0);
          syncAllInsuranceSwitches(cleanedCart);
          await refreshCartUI(cleanedCart);
          return;
        }

        if (insuranceItem && insuranceItem.quantity > 1) {
          const fixedCart = await changeInsuranceQuantity(insuranceItem.key, 1);
          syncAllInsuranceSwitches(fixedCart);
          await refreshCartUI(fixedCart);
          return;
        }

        if (!insuranceItem && nonInsuranceItems > 0 && localStorage.getItem(SB_INSURANCE_DECLINED_KEY) !== '1') {
          await autoAddInsuranceAfterRealItem();
          return;
        }

        syncAllInsuranceSwitches(cart);
      } catch (error) {
        console.error('[Seguro de envío] Error sincronizando:', error);
      }
    }

    window.fetch = async function sbShippingInsuranceFetchProxy(input, init) {
      const url = normalizeUrl(input);
      const isCartAdd = isCartAddUrl(url);
      const isCartMutation = isCartAdd || isCartChangeUrl(url);
      const isInsuranceRequest = isInsuranceAddRequest(input, init);

      const response = await originalFetch.apply(this, arguments);

      if (response && response.ok && isCartAdd && !isInsuranceRequest) {
        response.clone().json().catch(() => null).finally(() => {
          scheduleAutoInsuranceCheck();
        });
      } else if (response && response.ok && isCartMutation && !isInsuranceRequest) {
        setTimeout(syncFromCart, 180);
      }

      return response;
    };


    function scheduleAutoInsuranceCheck() {
      setTimeout(autoAddInsuranceAfterRealItem, 120);
      setTimeout(autoAddInsuranceAfterRealItem, 420);
      setTimeout(autoAddInsuranceAfterRealItem, 900);
    }

    document.addEventListener('submit', (event) => {
      const form = event.target && event.target.closest ? event.target.closest('form[action*="/cart/add"]') : null;
      if (!form) return;
      scheduleAutoInsuranceCheck();
    }, true);

    document.addEventListener('click', (event) => {
      const trigger = event.target && event.target.closest ? event.target.closest('button[type="submit"], [name="add"], [data-add-to-cart], product-form button, quick-add-modal button, quick-add button') : null;
      if (!trigger) return;
      scheduleAutoInsuranceCheck();
    }, true);

    document.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-sb-insurance-checkbox]');
      if (!checkbox) return;

      handleInsuranceToggle(checkbox);
    });

    document.addEventListener('cart:update', syncFromCart);
    document.addEventListener('cart:updated', syncFromCart);
    document.addEventListener('cart:refresh', syncFromCart);
    document.addEventListener('theme:cart:update', syncFromCart);
    document.addEventListener('theme:cart:refresh', syncFromCart);

    setTimeout(syncFromCart, 250);
  })();
